import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  useTheme,
  alpha,
  Skeleton,
  Button,
  Stack,
  Tooltip,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Hotel as HotelIcon,
  LocationOn as LocationOnIcon,
  Star as StarIcon,
  Bed as BedIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarTodayIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  PushPin as PushPinIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@utils/financialUtils';
import { Hotel } from '../../../types/types';

interface HotelGridProps {
  hotels: Hotel[];
  loading?: boolean;
  pinnedProperties?: Set<string>;
  onTogglePin?: (hotelId: string) => void;
  sortBy?: string;
  currency?: 'USD' | 'EUR';
}

const HotelGrid: React.FC<HotelGridProps> = ({
  hotels,
  loading = false,
  pinnedProperties = new Set(),
  onTogglePin,
  sortBy = 'lastUpdated',
  currency = 'USD'
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleViewHotel = (hotelId: string) => {
    navigate(`/hotels/${hotelId}`);
  };

  const handleEditHotel = (hotelId: string) => {
    navigate(`/hotels/${hotelId}/edit`);
  };

  const formatLocation = (hotel: Hotel) => {
    if (hotel.city && hotel.state) {
      return `${hotel.city}, ${hotel.state}`;
    }
    return hotel.address || 'Location not specified';
  };

  const getRecentRenovation = (hotel: Hotel) => {
    if (!hotel.renovation_history?.length) return null;

    const recent = hotel.renovation_history
      .sort((a, b) => (b.year || 0) - (a.year || 0))[0];

    return recent;
  };

  const getPropertyAge = (hotel: Hotel) => {
    if (!hotel.year_built) return null;
    const currentYear = new Date().getFullYear();
    return currentYear - hotel.year_built;
  };

  const getStatusInfo = (hotel: Hotel) => {
    // This would typically come from actual status data
    return {
      label: 'Active',
      color: 'success' as const,
      variant: 'filled' as const
    };
  };

  const HotelCard: React.FC<{ hotel: Hotel }> = ({ hotel }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const recentRenovation = getRecentRenovation(hotel);
    const roomCount = hotel.num_rooms || 0;
    const value = hotel.estimated_value || 0;
    const propertyAge = getPropertyAge(hotel);
    const status = getStatusInfo(hotel);
    const isPinned = pinnedProperties.has(hotel.id!);

    return (
      <Card
        elevation={0}
        sx={{
          height: '100%',
          borderRadius: 2,
          border: `1px solid ${isPinned ? theme.palette.warning.main : theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
            transform: 'translateY(-2px)',
            '& .hover-actions': {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}
        onClick={() => handleViewHotel(hotel.id!)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header with Avatar, Pin, and Status */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontSize: 16,
                  fontWeight: 600,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}
              >
                {hotel.name.charAt(0).toUpperCase()}
              </Avatar>

              {onTogglePin && (
                <Tooltip title={isPinned ? "Unpin Property" : "Pin Property"}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTogglePin(hotel.id!);
                    }}
                    sx={{
                      color: isPinned ? theme.palette.warning.main : theme.palette.text.secondary,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.warning.main, 0.1)
                      }
                    }}
                  >
                    <PushPinIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Chip
              label={status.label}
              color={status.color}
              variant={status.variant}
              size="small"
              sx={{
                fontWeight: 600,
                borderRadius: 1,
                height: 24
              }}
            />
          </Box>

          {/* Hotel Name and Type */}
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                lineHeight: 1.3,
                color: theme.palette.text.primary,
                fontSize: '1rem'
              }}
            >
              {hotel.name}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {hotel.property_type || 'Hotel'}
              </Typography>
            </Box>
          </Box>

          {/* Location */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {formatLocation(hotel)}
            </Typography>
          </Box>

          {/* Key Metrics Grid */}
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={6}>
              <Box sx={{
                textAlign: 'center',
                p: 1,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.05)
              }}>
                <BedIcon sx={{ fontSize: 16, color: theme.palette.primary.main, mb: 0.25 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.25 }}>
                  {roomCount.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Rooms
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{
                textAlign: 'center',
                p: 1,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.info.main, 0.05)
              }}>
                <CalendarTodayIcon sx={{ fontSize: 16, color: theme.palette.info.main, mb: 0.25 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.25 }}>
                  {propertyAge || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Years
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Value */}
          {value > 0 && (
            <Box sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.success.main, 0.05),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              mb: 1.5
            }}>
              <Typography variant="caption" color="success.main" sx={{ fontWeight: 600, mb: 0.25 }}>
                Estimated Value
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                {formatCurrency(value, currency)}
              </Typography>
            </Box>
          )}

          {/* KPI Badges */}
          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label="RevPAR: $125"
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
              <Chip
                label="NOI: 15%"
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
              <Chip
                label="Cap Rate: 7.2%"
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            </Stack>
          </Box>

          {/* Actions */}
          <Box sx={{ mt: 'auto', pt: 1.5 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewHotel(hotel.id!);
                }}
                sx={{
                  flex: 1,
                  borderRadius: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  py: 0.5
                }}
              >
                View
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditHotel(hotel.id!);
                }}
                sx={{
                  flex: 1,
                  borderRadius: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  py: 0.5
                }}
              >
                Edit
              </Button>
            </Stack>
          </Box>

          {/* Hover Actions Overlay */}
          <Box
            className="hover-actions"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: alpha(theme.palette.primary.main, 0.95),
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              opacity: 0,
              transform: 'translateY(10px)',
              transition: 'all 0.2s ease-in-out',
              pointerEvents: 'none',
              '&.show': {
                opacity: 1,
                transform: 'translateY(0)',
                pointerEvents: 'auto'
              }
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: theme.palette.common.white,
                fontWeight: 600,
                mb: 1
              }}
            >
              Quick Actions
            </Typography>
            <Stack spacing={1} sx={{ width: '80%' }}>
              <Button
                variant="contained"
                color="inherit"
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  // Run valuation action
                }}
                sx={{
                  backgroundColor: theme.palette.common.white,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.common.white, 0.9)
                  }
                }}
              >
                Run Valuation
              </Button>
              <Button
                variant="contained"
                color="inherit"
                size="small"
                startIcon={<EditIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditHotel(hotel.id!);
                }}
                sx={{
                  backgroundColor: theme.palette.common.white,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.common.white, 0.9)
                  }
                }}
              >
                Edit Details
              </Button>
              <Button
                variant="contained"
                color="inherit"
                size="small"
                startIcon={<AssessmentIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  // View report action
                }}
                sx={{
                  backgroundColor: theme.palette.common.white,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.common.white, 0.9)
                  }
                }}
              >
                View Report
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const LoadingCard = () => (
    <Card
      elevation={0}
      sx={{
        height: 320,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
        </Box>
        <Skeleton variant="text" width="85%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="70%" height={16} sx={{ mb: 1.5 }} />
        <Skeleton variant="text" width="90%" height={16} sx={{ mb: 1.5 }} />
        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          <Grid item xs={6}>
            <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
          </Grid>
          <Grid item xs={6}>
            <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
          </Grid>
        </Grid>
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1, mb: 1.5 }} />
        <Skeleton variant="rectangular" width="100%" height={32} sx={{ borderRadius: 1 }} />
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...Array(isTablet ? 4 : 6)].map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <LoadingCard />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (hotels.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          textAlign: 'center'
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            mb: 3
          }}
        >
          <HotelIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          No Properties Found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, lineHeight: 1.6 }}>
          No hotels match your current criteria. Try adjusting your filters or add a new property to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {hotels.map((hotel) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={hotel.id}>
          <HotelCard hotel={hotel} />
        </Grid>
      ))}
    </Grid>
  );
};

export default HotelGrid;